/**
 * Phone Number Utilities
 *
 * Handles parsing and formatting of phone numbers for forms and API requests.
 * Supports both legacy combined phone format and new separated fields.
 *
 * API Response Formats:
 * - Legacy: { phone: "+886 123456789" }
 * - New: { phoneCountryCode: "+886", phoneNumber: "123456789", phoneExtension: "123" }
 *
 * Form Field Format:
 * - phoneCountryCode: "+886"
 * - phoneNumber: "123456789"
 * - phoneExtension: "123" (optional)
 */

/**
 * Common country codes with their labels
 */
export const COUNTRY_CODE_OPTIONS = [
  { label: 'TW (+886)', value: '+886' },
  { label: 'US (+1)', value: '+1' },
  { label: 'UK (+44)', value: '+44' },
  { label: 'MY (+60)', value: '+60' },
  { label: 'SG (+65)', value: '+65' },
  { label: 'CN (+86)', value: '+86' },
  { label: 'JP (+81)', value: '+81' },
  { label: 'KR (+82)', value: '+82' },
  { label: 'AU (+61)', value: '+61' },
  { label: 'HK (+852)', value: '+852' },
] as const;

/**
 * Default country code when none is specified
 */
export const DEFAULT_COUNTRY_CODE = '+886';

/**
 * Parsed phone number result
 */
export interface ParsedPhone {
  countryCode: string;
  number: string;
  extension: string;
}

/**
 * Phone fields that can come from API response
 */
export interface PhoneApiFields {
  phone?: string | null; // Legacy combined format
  phoneCountryCode?: string | null;
  phoneNumber?: string | null;
  phoneExtension?: string | null;
}

/**
 * Phone form fields
 */
export interface PhoneFormFields {
  phoneCountryCode: string;
  phoneNumber: string;
  phoneExtension?: string;
}

/**
 * Parse a combined phone string into separate parts.
 * Handles formats like:
 * - "+886 123456789"
 * - "+1 (555) 123-4567"
 * - "+886 123456789 ext. 123"
 * - "123456789" (no country code)
 *
 * @param phone - Combined phone string
 * @param defaultCountryCode - Default country code if none found
 * @returns Parsed phone parts
 */
export function parsePhoneString(
  phone: string | null | undefined,
  defaultCountryCode: string = DEFAULT_COUNTRY_CODE
): ParsedPhone {
  if (!phone || phone.trim() === '') {
    return {
      countryCode: defaultCountryCode,
      number: '',
      extension: '',
    };
  }

  const trimmed = phone.trim();

  // Try to match country code at the start (e.g., "+886", "+1", "+44")
  const countryCodeMatch = trimmed.match(/^(\+\d{1,4})\s*(.*)$/);

  let countryCode = defaultCountryCode;
  let remainder = trimmed;

  if (countryCodeMatch) {
    countryCode = countryCodeMatch[1];
    remainder = countryCodeMatch[2];
  }

  // Try to extract extension (e.g., "ext. 123", "x123", "#123")
  const extensionMatch = remainder.match(/^(.+?)(?:\s*(?:ext\.?|x|#)\s*(\d+))?$/i);

  const number = extensionMatch ? extensionMatch[1].trim() : remainder;
  const extension = extensionMatch?.[2] || '';

  return {
    countryCode,
    number,
    extension,
  };
}

/**
 * Parse phone fields from API response.
 * Handles both legacy combined format and new separated fields.
 * Prefers new separated fields when available.
 *
 * @param apiFields - Phone fields from API response
 * @param defaultCountryCode - Default country code if none found
 * @returns Form-ready phone fields
 *
 * @example
 * ```tsx
 * // From API response with new fields
 * const phone = parsePhoneFromApi({
 *   phoneCountryCode: '+886',
 *   phoneNumber: '123456789',
 *   phoneExtension: '123'
 * });
 * // Result: { phoneCountryCode: '+886', phoneNumber: '123456789', phoneExtension: '123' }
 *
 * // From API response with legacy field
 * const phone = parsePhoneFromApi({ phone: '+886 123456789' });
 * // Result: { phoneCountryCode: '+886', phoneNumber: '123456789', phoneExtension: '' }
 * ```
 */
export function parsePhoneFromApi(
  apiFields: PhoneApiFields,
  defaultCountryCode: string = DEFAULT_COUNTRY_CODE
): PhoneFormFields {
  // Prefer new separated fields if available
  if (apiFields.phoneCountryCode || apiFields.phoneNumber) {
    return {
      phoneCountryCode: apiFields.phoneCountryCode || defaultCountryCode,
      phoneNumber: apiFields.phoneNumber || '',
      phoneExtension: apiFields.phoneExtension || '',
    };
  }

  // Fall back to parsing legacy combined phone field
  const parsed = parsePhoneString(apiFields.phone, defaultCountryCode);

  return {
    phoneCountryCode: parsed.countryCode,
    phoneNumber: parsed.number,
    phoneExtension: parsed.extension,
  };
}

/**
 * Format phone fields into a combined string for display.
 *
 * @param fields - Phone form fields
 * @param includeExtension - Whether to include extension in output
 * @returns Formatted phone string
 *
 * @example
 * ```tsx
 * formatPhoneForDisplay({ phoneCountryCode: '+886', phoneNumber: '123456789', phoneExtension: '123' });
 * // Result: "+886 123456789 ext. 123"
 *
 * formatPhoneForDisplay({ phoneCountryCode: '+886', phoneNumber: '123456789' });
 * // Result: "+886 123456789"
 * ```
 */
export function formatPhoneForDisplay(
  fields: PhoneFormFields,
  includeExtension: boolean = true
): string {
  if (!fields.phoneNumber) {
    return '';
  }

  let result = `${fields.phoneCountryCode} ${fields.phoneNumber}`;

  if (includeExtension && fields.phoneExtension) {
    result += ` ext. ${fields.phoneExtension}`;
  }

  return result;
}

/**
 * Format phone fields for API request (legacy combined format).
 * Use this when the API expects the legacy `phone` field.
 *
 * @param fields - Phone form fields
 * @returns Combined phone string for API
 */
export function formatPhoneForApiLegacy(fields: PhoneFormFields): string {
  if (!fields.phoneNumber) {
    return '';
  }

  return `${fields.phoneCountryCode} ${fields.phoneNumber}`;
}

/**
 * Format phone fields for API request (new separated format).
 * Use this when the API accepts separated phone fields.
 *
 * @param fields - Phone form fields
 * @returns Separated phone fields for API
 */
export function formatPhoneForApi(fields: PhoneFormFields): {
  phoneCountryCode: string;
  phoneNumber: string;
  phoneExtension: string;
} {
  return {
    phoneCountryCode: fields.phoneCountryCode || DEFAULT_COUNTRY_CODE,
    phoneNumber: fields.phoneNumber || '',
    phoneExtension: fields.phoneExtension || '',
  };
}

/**
 * Check if phone number is valid (basic validation).
 * Checks that there's at least a phone number present.
 *
 * @param fields - Phone form fields
 * @returns True if phone number is present
 */
export function isPhoneValid(fields: PhoneFormFields): boolean {
  return Boolean(fields.phoneNumber && fields.phoneNumber.trim().length > 0);
}

/**
 * Get country code options for Select component with i18n support.
 *
 * @param t - Translation function
 * @param namespace - Translation namespace for country code labels
 * @returns Country code options for Select component
 *
 * @example
 * ```tsx
 * const options = getCountryCodeOptions(t, 'vendor.countryCodeLabels');
 * // Uses keys like: vendor.countryCodeLabels.tw, vendor.countryCodeLabels.us, etc.
 * ```
 */
export function getCountryCodeOptions(
  t: (key: string) => string,
  namespace: string = 'vendor.countryCodeLabels'
): Array<{ label: string; value: string }> {
  return [
    { label: t(`${namespace}.tw`), value: '+886' },
    { label: t(`${namespace}.us`), value: '+1' },
    { label: t(`${namespace}.uk`), value: '+44' },
    { label: t(`${namespace}.my`), value: '+60' },
    { label: t(`${namespace}.sg`), value: '+65' },
    { label: t(`${namespace}.cn`), value: '+86' },
  ];
}

/**
 * Mobile phone fields from API
 */
export interface MobileApiFields {
  mobile?: string | null; // Legacy combined format
  mobileCountryCode?: string | null;
  mobileNumber?: string | null;
}

/**
 * Mobile form fields
 */
export interface MobileFormFields {
  mobileCountryCode: string;
  mobileNumber: string;
}

/**
 * Parse mobile fields from API response.
 * Handles both legacy combined format and new separated fields.
 *
 * @param apiFields - Mobile fields from API response
 * @param defaultCountryCode - Default country code if none found
 * @returns Form-ready mobile fields
 */
export function parseMobileFromApi(
  apiFields: MobileApiFields,
  defaultCountryCode: string = DEFAULT_COUNTRY_CODE
): MobileFormFields {
  // Prefer new separated fields if available
  if (apiFields.mobileCountryCode || apiFields.mobileNumber) {
    return {
      mobileCountryCode: apiFields.mobileCountryCode || defaultCountryCode,
      mobileNumber: apiFields.mobileNumber || '',
    };
  }

  // Fall back to parsing legacy combined mobile field
  const parsed = parsePhoneString(apiFields.mobile, defaultCountryCode);

  return {
    mobileCountryCode: parsed.countryCode,
    mobileNumber: parsed.number,
  };
}

/**
 * Format mobile fields for display.
 *
 * @param fields - Mobile form fields
 * @returns Formatted mobile string
 */
export function formatMobileForDisplay(fields: MobileFormFields): string {
  if (!fields.mobileNumber) {
    return '';
  }
  return `${fields.mobileCountryCode} ${fields.mobileNumber}`;
}

/**
 * Format phone for display from API response.
 * Handles both legacy and new API formats.
 *
 * @param apiFields - Phone fields from API
 * @param extLabel - Extension label (default: "ext.")
 * @returns Formatted phone string or empty string
 */
export function formatPhoneFromApiForDisplay(
  apiFields: PhoneApiFields,
  extLabel: string = 'ext.'
): string {
  // Prefer new separated fields
  if (apiFields.phoneCountryCode && apiFields.phoneNumber) {
    let result = `${apiFields.phoneCountryCode} ${apiFields.phoneNumber}`;
    if (apiFields.phoneExtension) {
      result += ` ${extLabel} ${apiFields.phoneExtension}`;
    }
    return result;
  }

  // Fall back to legacy phone field
  if (apiFields.phone) {
    return apiFields.phone;
  }

  return '';
}

/**
 * Format mobile for display from API response.
 * Handles both legacy and new API formats.
 *
 * @param apiFields - Mobile fields from API
 * @returns Formatted mobile string or empty string
 */
export function formatMobileFromApiForDisplay(apiFields: MobileApiFields): string {
  // Prefer new separated fields
  if (apiFields.mobileCountryCode && apiFields.mobileNumber) {
    return `${apiFields.mobileCountryCode} ${apiFields.mobileNumber}`;
  }

  // Fall back to legacy mobile field
  if (apiFields.mobile) {
    return apiFields.mobile;
  }

  return '';
}
